import { AlertCircle } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DialogBody, DialogFooter } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import UploadInput from "@/features/file-upload/components/upload-input";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { queryClient } from "@/lib/react-query";
import { HttpError, isBadRequest } from "@/types/error";
import { PublicRecording } from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import { getRecordingQueryKey } from "../api/get-recording";
import { useUpdateRecording } from "../api/update-recording";
import {
  UploadCustomThumbnailFormData,
  uploadCustomThumbnailSchema,
} from "../types/upload-custom-thumbnail-schema";

type UploadCustomThumbnailProps = {
  recordingId: string;
};

export function UploadCustomThumbnail({
  recordingId,
}: UploadCustomThumbnailProps) {
  const form = useForm<UploadCustomThumbnailFormData>({
    resolver: zodResolver(uploadCustomThumbnailSchema),
    defaultValues: {
      thumbnailURL: "",
    },
  });
  const { organisation } = useOrganisation();

  const {
    mutateAsync: updateRecording,
    isError,
    isPending,
    error,
  } = useUpdateRecording({
    onSuccess: (_, { data }) => {
      queryClient.setQueryData(
        getRecordingQueryKey(organisation.id, recordingId),
        (oldData: PublicRecording) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            imageURL: data.imageURL,
          };
        },
      );
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  async function handleUpdateRecording(data: UploadCustomThumbnailFormData) {
    try {
      await updateRecording({
        orgId: organisation.id,
        id: recordingId,
        data: {
          imageURL: data.thumbnailURL,
        },
      });
    } catch (error) {
      const httpError = error as HttpError;
      if (isBadRequest(httpError)) {
        form.setError("root", { message: httpError.message.join(",") });
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleUpdateRecording)}>
        <DialogBody>
          <FormField
            control={form.control}
            name="thumbnailURL"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Upload custom thumbnail</FormLabel>
                <FormDescription>
                  You can upload .jpeg, .png, .gif, or .webp files. Recommended
                  size is 1280x720.
                </FormDescription>
                <FormControl>
                  <UploadInput
                    name="thumbnailURL"
                    onChange={field.onChange}
                    value={field.value}
                    allowedFileTypes={[
                      "image/jpeg",
                      "image/png",
                      "image/gif",
                      "image/webp",
                    ]}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {isError && (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Unexpected error</AlertTitle>
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          )}
        </DialogBody>
        <DialogFooter className="sm:justify-end">
          <Button variant="default" isLoading={isPending}>
            {isPending ? "Saving..." : "Set Thumbnail"}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
